import { NoticeLoading } from '@fs/zion-icon/dist/cjs/icons'
import {
  DialogOverlay,
  DialogOverlayActions,
  DialogOverlayFooter,
  ExpandableListItem,
  List,
  ListItem,
  Paragraph,
  Separator,
  useOverlay,
  Button,
} from '@fs/zion-ui'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useNotifications from '../../../hooks/useNotifications'
import { getUserRollbackResults } from '../../../services/userService'
import { toLocaleString, UTCDateStringToTimestamp } from '../../../util/DateUtil'
import { camelCaseToReadable, capitalizeFirstLetter } from '../../../util/StringUtil'
import { millisToMinutesAndSeconds } from '../../../util/Utils'
import DmcDataView from '../../common/DmcDataView'
import { UserContext } from '../UserContext'

export default function UserRollbackResultsButtonAndDialog() {
  const { t } = useTranslation()
  const { error } = useNotifications()
  const { currentUser } = useContext(UserContext)
  const overlay = useOverlay()
  const [userRollbackResults, setUserRollbackResults] = useState()

  useEffect(() => {
    const fetchAndDisplayData = async () => {
      try {
        const userRollbackResultsResponse = await getUserRollbackResults(currentUser.contributorId)
        setUserRollbackResults(userRollbackResultsResponse.data)
      } catch (err) {
        setUserRollbackResults(null)
        error(
          t('user.errors.retrievingUserRollbackResults', {
            defaultValue: `Error retrieving user rollback results: ${err.message || ''}`,
            message: err.message || '',
          }),
          err
        )
      }
    }
    if (overlay.isOpen && overlay.transitioning && !userRollbackResults) {
      fetchAndDisplayData()
    }
    if (!overlay.isOpen && overlay.transitioning && userRollbackResults) {
      setUserRollbackResults()
    }
  }, [currentUser.contributorId, error, overlay.isOpen, overlay.transitioning, t, userRollbackResults])

  const formatValue = (key, value) => {
    if (key.includes('Timestamp')) {
      return toLocaleString(new Date(value), true)
    }
    if (key === 'startDate') {
      return toLocaleString(new Date(UTCDateStringToTimestamp(value)), true)
    }
    if (key === 'durationInMs') {
      return millisToMinutesAndSeconds(value)
    }
    return value
  }

  const extractSummaryFields = (results) => {
    return Object.entries(results).filter(([, value]) => {
      return typeof value !== 'object'
    })
  }

  const extractActionResultsFields = (results) => {
    return Object.entries(results).filter(([key, value]) => {
      return typeof value === 'object' && key !== 'previousResults'
    })
  }

  const summaryFields = userRollbackResults ? extractSummaryFields(userRollbackResults) : []
  const actionResultsFields = userRollbackResults ? extractActionResultsFields(userRollbackResults) : []
  const previousSummaryFields = userRollbackResults?.previousResults
    ? extractSummaryFields(userRollbackResults.previousResults)
    : []
  const previousActionResultsFields = userRollbackResults?.previousResults
    ? extractActionResultsFields(userRollbackResults.previousResults)
    : []

  const summaryData = summaryFields.map(([key, value]) => {
    const formattedValue = formatValue(key, value)
    return {
      label: t(`user.rollback.results.resultField.${key}`, camelCaseToReadable(key)),
      detail: formattedValue,
      key,
    }
  })

  const previousSummaryData = previousSummaryFields.map(([key, value]) => {
    const formattedValue = formatValue(key, value)
    return {
      label: t(`user.rollback.results.resultField.${key}`, camelCaseToReadable(key)),
      detail: formattedValue,
      key,
    }
  })

  return (
    <>
      <Button id="rollResBtn" emphasis="medium" onClick={overlay.handleClick}>
        {t('user.rollback.results.results', 'Rollback Results')}
      </Button>
      <DialogOverlay
        id="rollbackResultsOverlay"
        {...overlay}
        title={t('user.rollback.results.results', 'Rollback Results')}
        size="xxl"
      >
        {userRollbackResults === undefined && <NoticeLoading size="lg" />}
        {userRollbackResults === null && (
          <Paragraph centered size="sm">
            {t('user.rollback.results.notFound', 'Results not found (rollback not run, failed, or is in process)')}
          </Paragraph>
        )}
        {userRollbackResults && (
          <>
            <DmcDataView data={summaryData} />
            <Separator size="xs" />
            <List>
              {actionResultsFields.map(([actionStatus, commands]) => {
                return (
                  <ExpandableListItem primaryText={capitalizeFirstLetter(actionStatus)} key={actionStatus}>
                    {Object.entries(commands).map(([type, count]) => {
                      return <ListItem key={type} primaryText={type} secondaryText={count} />
                    })}
                  </ExpandableListItem>
                )
              })}
              {previousSummaryFields.length > 0 && (
                <>
                  <Separator size="xs" />
                  <Paragraph>{t('user.rollback.results.previousResults', 'Previous Results')}</Paragraph>
                  <DmcDataView data={previousSummaryData} />
                  <Separator size="xs" />
                  <List>
                    {previousActionResultsFields.map(([actionStatus, commands]) => {
                      return (
                        <ExpandableListItem primaryText={capitalizeFirstLetter(actionStatus)} key={actionStatus}>
                          {Object.entries(commands).map(([type, count]) => {
                            return <ListItem key={type} primaryText={type} secondaryText={count} />
                          })}
                        </ExpandableListItem>
                      )
                    })}
                  </List>
                </>
              )}
            </List>
          </>
        )}
        <DialogOverlayFooter>
          <DialogOverlayActions>
            <Button emphasis="medium" onClick={overlay.close}>
              {t('action.done', 'Done')}
            </Button>
          </DialogOverlayActions>
        </DialogOverlayFooter>
      </DialogOverlay>
    </>
  )
}
